import CryptoJS from "crypto-js";

// MMG Configuration - These would typically come from environment variables
const MMG_CONFIG = {
  baseUrl: "https://checkout.mmg.gy", // Replace with actual MMG checkout URL
  clientId: process.env.REACT_APP_MMG_CLIENT_ID || "YOUR_CLIENT_ID",
  merchantId: process.env.REACT_APP_MMG_MERCHANT_ID || "YOUR_MERCHANT_ID",
  secretKey: process.env.REACT_APP_MMG_SECRET_KEY || "YOUR_SECRET_KEY",
  merchantName: "NourishGY",
  responseUrl: `${window.location.origin}/payment/callback`,
  errorUrl: `${window.location.origin}/payment/error`,
  // These would be provided by MMG during onboarding
  mmgPublicKey: process.env.REACT_APP_MMG_PUBLIC_KEY || "YOUR_MMG_PUBLIC_KEY",
  // Your private key for decryption
  privateKey: process.env.REACT_APP_PRIVATE_KEY || "YOUR_PRIVATE_KEY",
};

class MMGPaymentService {
  /**
   * Generate a unique transaction ID
   */
  generateTransactionId() {
    return `nourishgy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create the payment request object
   */
  createPaymentRequest(amount, description, subscriptionId) {
    const requestInitiatorTime = new Date().getTime().toString();

    return {
      secretKey: MMG_CONFIG.secretKey,
      amount: amount.toString(),
      merchantId: MMG_CONFIG.merchantId,
      merchantTransactionId: this.generateTransactionId(),
      productDescription: description,
      requestInitiationTime: requestInitiatorTime,
      merchantName: MMG_CONFIG.merchantName,
      subscriptionId: subscriptionId, // Custom field for tracking
    };
  }

  /**
   * Encrypt data using RSA (simplified version - in production, use proper RSA library)
   * Note: This is a simplified implementation. In production, you should use a proper RSA library
   * like node-rsa or crypto-js with proper RSA encryption
   */
  encryptData(data) {
    try {
      const jsonString = JSON.stringify(data);
      // For now, using AES encryption as a placeholder
      // In production, replace this with proper RSA encryption using MMG's public key
      const encrypted = CryptoJS.AES.encrypt(
        jsonString,
        MMG_CONFIG.mmgPublicKey
      ).toString();
      return encrypted;
    } catch (error) {
      console.error("Encryption error:", error);
      throw new Error("Failed to encrypt payment data");
    }
  }

  /**
   * Decrypt response data using your private key
   */
  decryptResponse(encryptedData) {
    try {
      // For now, using AES decryption as a placeholder
      // In production, replace this with proper RSA decryption using your private key
      const decrypted = CryptoJS.AES.decrypt(
        encryptedData,
        MMG_CONFIG.privateKey
      ).toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch (error) {
      console.error("Decryption error:", error);
      throw new Error("Failed to decrypt payment response");
    }
  }

  /**
   * Generate the MMG checkout URL
   */
  generateCheckoutUrl(amount, description, subscriptionId) {
    try {
      const paymentRequest = this.createPaymentRequest(
        amount,
        description,
        subscriptionId
      );
      const encryptedToken = this.encryptData(paymentRequest);

      // Base64 URL encode the token
      const encodedToken = btoa(encryptedToken)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");

      const checkoutUrl = `${MMG_CONFIG.baseUrl}?X-Client-ID=${MMG_CONFIG.clientId}&token=${encodedToken}&merchantId=${MMG_CONFIG.merchantId}`;

      return {
        url: checkoutUrl,
        transactionId: paymentRequest.merchantTransactionId,
      };
    } catch (error) {
      console.error("Error generating checkout URL:", error);
      throw new Error("Failed to generate payment URL");
    }
  }

  /**
   * Handle payment callback response
   */
  handlePaymentCallback(encryptedToken) {
    try {
      // Base64 URL decode
      const decodedToken = atob(
        encryptedToken.replace(/-/g, "+").replace(/_/g, "/")
      );

      // Decrypt the response
      const decryptedResponse = this.decryptResponse(decodedToken);

      return {
        success: decryptedResponse.ResultCode === "0",
        resultCode: decryptedResponse.ResultCode,
        resultMessage: decryptedResponse.ResultMessage,
        transactionId: decryptedResponse.transactionId,
        merchantTransactionId: decryptedResponse.merchantTransactionId,
        htmlResponse: decryptedResponse.htmlResponse,
      };
    } catch (error) {
      console.error("Error handling payment callback:", error);
      return {
        success: false,
        error: "Failed to process payment response",
      };
    }
  }

  /**
   * Get MMG OAuth token for API calls
   */
  async getOAuthToken(apiKey, username, password) {
    try {
      const response = await fetch(
        "https://gtt-uat-oauth2-service-api.qpass.com:9143/oauth2-endpoint/oauth/resourcetoken",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "password",
            api_key: apiKey,
            username: username,
            password: password,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get OAuth token");
      }

      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error("OAuth token error:", error);
      throw new Error("Failed to authenticate with MMG");
    }
  }

  /**
   * Process manual payment to vendor (for NourishGY admin use)
   */
  async processVendorPayment(vendorId, amount, transactionId, oauthToken) {
    try {
      // This would be implemented based on MMG's vendor payment API
      // For now, this is a placeholder
      console.log("Processing vendor payment:", {
        vendorId,
        amount,
        transactionId,
        oauthToken,
      });

      // In production, this would make an API call to MMG to send payment to vendor
      return {
        success: true,
        vendorTransactionId: `vendor_${Date.now()}`,
        message: "Payment processed to vendor",
      };
    } catch (error) {
      console.error("Vendor payment error:", error);
      throw new Error("Failed to process vendor payment");
    }
  }
}

export default new MMGPaymentService();
